// src/components/SyntraMfgAssets.jsx
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faIndustry,
  faWarehouse,
  faHandshake,
  faTools,
  faFileContract,
  faGlobe,
  faTimes,
  faPhone,
  faCalendarAlt,
  faMapMarkerAlt,
} from "@fortawesome/free-solid-svg-icons";

// Fully functional Manufacturing Assets Hub component
export default function SyntraMfgAssets() {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null); // selected asset card
  const [modalMode, setModalMode] = useState(null); // e.g., 'listings','lease','equipment','storage','partnership','global','post'
  const [feed, setFeed] = useState([]);
  const [dailyBonusClaimed, setDailyBonusClaimed] = useState(false);

  // Dummy data for manufacturing assets
  const assets = [
    {
      id: "factories",
      title: "Buy Manufacturing Units",
      desc: "Verified manufacturing units and factories available for sale across regions.",
      icon: faIndustry,
      action: "Explore Listings",
      img: "https://via.placeholder.com/800x480?text=Factory",
      listings: [
        {
          id: "F-101",
          area: "5000 sq ft",
          price: 35000000,
          location: "Industrial Zone A",
          img: "https://via.placeholder.com/400x250?text=Factory+F-101",
        },
        {
          id: "F-202",
          area: "3000 sq ft",
          price: 21000000,
          location: "Industrial Zone B",
          img: "https://via.placeholder.com/400x250?text=Factory+F-202",
        },
      ],
    },
    {
      id: "lease",
      title: "Lease / Rent Industrial Space",
      desc: "Short and long-term leasing options for manufacturing & warehousing.",
      icon: faFileContract,
      action: "Find Space to Lease",
      img: "https://via.placeholder.com/800x480?text=Lease+Space",
      leases: [
        { id: "L-01", area: "2000 sq ft", monthly: 150000, term: "6-12 months", location: "Zone X" },
        { id: "L-02", area: "1000 sq ft", monthly: 80000, term: "3-6 months", location: "Zone Y" },
      ],
    },
    {
      id: "machinery",
      title: "Machinery Rental",
      desc: "Industrial machinery, forklifts, CNC machines, 3D printers on rental basis.",
      icon: faTools,
      action: "View Equipment",
      img: "https://via.placeholder.com/800x480?text=Machinery",
      equipment: [
        { id: "M-CNC1", name: "CNC Lathe", rateDay: 5000, provider: "IndMach Rentals", img: "https://via.placeholder.com/400x250?text=CNC+Lathe" },
        { id: "M-FLK1", name: "Forklift", rateDay: 2000, provider: "LiftPro", img: "https://via.placeholder.com/400x250?text=Forklift" },
      ],
    },
    {
      id: "storage",
      title: "Warehousing & Cold Storage",
      desc: "Rent or invest in warehouses, silos, and cold storage facilities for industrial goods.",
      icon: faWarehouse,
      action: "Check Facilities",
      img: "https://via.placeholder.com/800x480?text=Storage",
      storage: [
        { id: "S-1", name: "Cold Storage A", capacityTons: 50, rentPerDay: 2000, location: "Industrial Hub" },
        { id: "S-2", name: "Warehouse B", capacityTons: 100, rentPerDay: 3500, location: "Industrial Zone" },
      ],
    },
    {
      id: "partnership",
      title: "Joint Manufacturing Ventures",
      desc: "Partner with other manufacturers for revenue sharing, co-production, and sustainable projects.",
      icon: faHandshake,
      action: "Start Partnership",
      img: "https://via.placeholder.com/800x480?text=Partnership",
      programs: [
        { id: "P-man1", name: "Electronics Assembly JV", share: "50/50", minArea: "1000 sq ft" },
        { id: "P-man2", name: "Plastic Molding Partnership", share: "40/60", minArea: "500 sq ft" },
      ],
    },
    {
      id: "global",
      title: "Global Manufacturing Assets",
      desc: "Cross-border industrial and manufacturing investment opportunities.",
      icon: faGlobe,
      action: "View Opportunities",
      img: "https://via.placeholder.com/800x480?text=Global+Assets",
      global: [
        { id: "G-IN-1", country: "Vietnam", area: "2000 sq ft", priceUSD: 80000 },
        { id: "G-IN-2", country: "Poland", area: "1500 sq ft", priceUSD: 120000 },
      ],
    },
  ];

  // Activity feed helper
  const announce = (text) => setFeed((f) => [{ ts: Date.now(), text }, ...f].slice(0, 60));

  // Modal handlers
  const openModal = (asset, mode) => { setSelected(asset); setModalMode(mode); };
  const closeModal = () => { setSelected(null); setModalMode(null); };

  // Shared form states
  const [contactForm, setContactForm] = useState({ name: "", phone: "", message: "" });
  const [offerForm, setOfferForm] = useState({ listingId: "", offerAmount: "" });
  const [leaseForm, setLeaseForm] = useState({ leaseId: "", months: 6, name: "", phone: "" });
  const [rentForm, setRentForm] = useState({ equipId: "", days: 1, name: "", phone: "" });
  const [storageForm, setStorageForm] = useState({ storageId: "", days: 1, name: "", phone: "" });
  const [partnershipForm, setPartnershipForm] = useState({ programId: "", name: "", phone: "" });
  const [postAssetForm, setPostAssetForm] = useState({ title: "", desc: "", contact: "" });

  // Action handlers
  const handleContact = () => {
    if (!contactForm.name || !contactForm.phone) return alert("Please fill name & phone.");
    announce(`Contact request sent: ${contactForm.name} — ${contactForm.phone}. Message: ${contactForm.message || '(none)'}`);
    setContactForm({ name: "", phone: "", message: "" }); closeModal(); alert("Contact submitted (dummy).");
  };
  const handleOffer = () => {
    if (!offerForm.offerAmount) return alert("Enter offer amount.");
    announce(`Offer placed for ${offerForm.listingId || "listing"}: ₹${offerForm.offerAmount}`);
    setOfferForm({ listingId: "", offerAmount: "" }); alert("Offer submitted (dummy).");
  };
  const handleLeaseApply = () => {
    if (!leaseForm.name || !leaseForm.phone) return alert("Fill contact info.");
    announce(`Lease application ${leaseForm.leaseId} by ${leaseForm.name} for ${leaseForm.months} months`);
    setLeaseForm({ leaseId: "", months: 6, name: "", phone: "" }); alert("Lease application submitted (dummy).");
  };
  const handleRent = () => {
    if (!rentForm.name || !rentForm.phone) return alert("Fill contact info.");
    announce(`Equipment ${rentForm.equipId} rented for ${rentForm.days} days by ${rentForm.name}`);
    setRentForm({ equipId: "", days: 1, name: "", phone: "" }); alert("Equipment booked (dummy).");
  };
  const handleStorageBook = () => {
    if (!storageForm.name || !storageForm.phone) return alert("Fill contact info.");
    announce(`Storage ${storageForm.storageId} booked for ${storageForm.days} days by ${storageForm.name}`);
    setStorageForm({ storageId: "", days: 1, name: "", phone: "" }); alert("Storage booked (dummy).");
  };
  const handlePartnership = () => {
    if (!partnershipForm.name || !partnershipForm.phone) return alert("Fill contact info.");
    announce(`Partnership interest ${partnershipForm.programId} by ${partnershipForm.name}`);
    setPartnershipForm({ programId: "", name: "", phone: "" }); alert("Partnership request submitted (dummy).");
  };
  const handlePostAsset = () => {
    if (!postAssetForm.title || !postAssetForm.contact) return alert("Provide title and contact.");
    announce(`New asset posted: ${postAssetForm.title} — Contact: ${postAssetForm.contact}`);
    setPostAssetForm({ title: "", desc: "", contact: "" }); alert("Asset posted (dummy)."); closeModal();
  };
  const claimDailyBonus = () => {
    if (dailyBonusClaimed) return alert("Daily bonus already claimed.");
    setDailyBonusClaimed(true); announce("Daily bonus claimed: ₹50 (dummy)"); alert("Daily bonus ₹50 added (dummy).");
  };

  // Helper
  const fmt = (n) => `₹${n.toLocaleString()}`;
  const filtered = assets.filter(a => a.title.toLowerCase().includes(query.toLowerCase()) || a.desc.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="min-h-screen bg-gradient-to-r from-cyan-700 to-cyan-900 py-6 px-4 md:px-20">
      <style>{`.card-hit{transition: transform .18s ease, box-shadow .18s ease}.card-hit:hover{transform: translateY(-6px); box-shadow:0 18px 40px rgba(2,6,23,0.2)}`}</style>
      
      {/* Header */}
        <div className="bg-cyan-600 rounded-lg px-4 py-2">
          <h1 className="text-2xl md:text-3xl font-bold text-yellow-200 flex items-center gap-3">🏭 SyntraMfg - Manufacturing Assets Hub</h1>
          <p className="mt-3 text-white max-w-2xl">Marketplace for factories, industrial machinery, warehouses, partnerships & global investments. Use cards to explore and interact.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center px-4 py-2 rounded-lg bg-gray-300 mt-2 mb-2">
            <FontAwesomeIcon icon={faMapMarkerAlt} className="text-cyan-700 mr-2" />
            <input placeholder="Enter PIN or locality" className="bg-black text-white h-10 px-4 py-2" onChange={e => setQuery(e.target.value)} value={query} />
            <button onClick={() => openModal(null, 'post')} className="bg-cyan-400 text-white hover:bg-cyan-500 px-4 py-2 rounded mt-4">Post Your Asset</button>
            <button onClick={claimDailyBonus} className="bg-cyan-400 text-white hover:bg-cyan-500 px-4 py-2 rounded mt-4">Claim Daily Bonus</button>
        </div>

      {/* Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto mb-8">
        {filtered.map(a => (
          <div key={a.id} className="card-hit rounded-2xl shadow-lg bg-gray-300 p-4 flex flex-col justify-between">
            <img src={a.img} alt={a.title} className="rounded-xl w-full h-40 object-cover mb-3" />
            <div>
              <div className="flex items-center gap-3 mb-2">
                <FontAwesomeIcon icon={a.icon} className="w-7 h-7 text-cyan-600" />
                <h3 className="text-lg font-semibold text-cyan-800">{a.title}</h3>
              </div>
              <p className="text-sm text-cyan-600 mb-3">{a.desc}</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => openModal(a, a.id === 'factories' ? 'listings' : a.id)} className="flex-1 bg-cyan-800 text-white rounded-xl py-2">{a.action}</button>
            </div>
          </div>
        ))}
      </div>

      {/* Activity Feed */}
      <div className="max-w-6xl mx-auto mb-6">
        <h2 className="text-xl font-bold text-yellow-300 mb-2">Activity Feed</h2>
        <div className="bg-cyan-800 text-cyan-200 rounded p-3 max-h-64 overflow-y-auto">
          {feed.map(f => (
            <div key={f.ts} className="border-b border-cyan-700 py-1 text-sm">{new Date(f.ts).toLocaleTimeString()} - {f.text}</div>
          ))}
        </div>
      </div>

      {/* Modals */}
      {selected && modalMode && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-start pt-20 z-50 overflow-auto">
          <div className="bg-gray-300 rounded-xl shadow-xl max-w-3xl w-full mx-4 p-6 relative">
            <button onClick={closeModal} className="absolute top-3 right-3 text-cyan-500 hover:text-cyan-800">
              <FontAwesomeIcon icon={faTimes} size="lg" />
            </button>

            {/* Factory Listings */}
            {modalMode === 'listings' && selected.listings && (
              <>
                <h3 className="text-2xl font-bold mb-3">{selected.title}</h3>
                {selected.listings.map(l => (
                  <div key={l.id} className="border rounded p-3 mb-3 flex flex-col md:flex-row md:items-center gap-3">
                    <img src={l.img} alt={l.id} className="w-full md:w-48 h-32 object-cover rounded" />
                    <div className="flex-1">
                      <p><strong>ID:</strong> {l.id}</p>
                      <p><strong>Area:</strong> {l.area}</p>
                      <p><strong>Price:</strong> {fmt(l.price)}</p>
                      <p><strong>Location:</strong> {l.location}</p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <input type="number" placeholder="Offer ₹" className="border p-1 rounded" value={offerForm.listingId === l.id ? offerForm.offerAmount : ""} onChange={e => setOfferForm({ listingId: l.id, offerAmount: e.target.value })} />
                      <button onClick={handleOffer} className="bg-yellow-400 py-1 rounded">Place Offer</button>
                      <button onClick={() => { setContactForm({}); openModal(selected,'contact') }} className="bg-cyan-800 text-white py-1 rounded">Contact Owner</button>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Lease Modal */}
            {modalMode === 'lease' && selected.leases && (
              <>
                <h3 className="text-2xl font-bold mb-3">{selected.title}</h3>
                {selected.leases.map(l => (
                  <div key={l.id} className="border rounded p-3 mb-3 flex flex-col md:flex-row md:items-center gap-3">
                    <div className="flex-1">
                      <p><strong>ID:</strong> {l.id}</p>
                      <p><strong>Area:</strong> {l.area}</p>
                      <p><strong>Monthly Rent:</strong> {fmt(l.monthly)}</p>
                      <p><strong>Term:</strong> {l.term}</p>
                      <p><strong>Location:</strong> {l.location}</p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <input type="text" placeholder="Your Name" className="border p-1 rounded" value={leaseForm.name} onChange={e => setLeaseForm(prev => ({ ...prev, name: e.target.value }))} />
                      <input type="text" placeholder="Phone" className="border p-1 rounded" value={leaseForm.phone} onChange={e => setLeaseForm(prev => ({ ...prev, phone: e.target.value }))} />
                      <input type="number" placeholder="Months" className="border p-1 rounded" value={leaseForm.months} onChange={e => setLeaseForm(prev => ({ ...prev, months: e.target.value }))} />
                      <button onClick={() => { setLeaseForm(prev => ({ ...prev, leaseId: l.id })); handleLeaseApply(); }} className="bg-yellow-400 py-1 rounded">Apply</button>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Machinery Rental */}
            {modalMode === 'machinery' && selected.equipment && (
              <>
                <h3 className="text-2xl font-bold mb-3">{selected.title}</h3>
                {selected.equipment.map(e => (
                  <div key={e.id} className="border rounded p-3 mb-3 flex flex-col md:flex-row md:items-center gap-3">
                    <img src={e.img} alt={e.name} className="w-full md:w-48 h-32 object-cover rounded" />
                    <div className="flex-1">
                      <p><strong>Name:</strong> {e.name}</p>
                      <p><strong>Provider:</strong> {e.provider}</p>
                      <p><strong>Rate/Day:</strong> {fmt(e.rateDay)}</p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <input type="text" placeholder="Name" className="border p-1 rounded" value={rentForm.name} onChange={e => setRentForm(prev => ({ ...prev, name: e.target.value }))} />
                      <input type="text" placeholder="Phone" className="border p-1 rounded" value={rentForm.phone} onChange={e => setRentForm(prev => ({ ...prev, phone: e.target.value }))} />
                      <input type="number" placeholder="Days" className="border p-1 rounded" value={rentForm.days} onChange={e => setRentForm(prev => ({ ...prev, days: e.target.value }))} />
                      <button onClick={() => { setRentForm(prev => ({ ...prev, equipId: e.id })); handleRent(); }} className="bg-yellow-400 py-1 rounded">Rent</button>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Warehousing / Storage */}
            {modalMode === 'storage' && selected.storage && (
              <>
                <h3 className="text-2xl font-bold mb-3">{selected.title}</h3>
                {selected.storage.map(s => (
                  <div key={s.id} className="border rounded p-3 mb-3 flex flex-col md:flex-row md:items-center gap-3">
                    <div className="flex-1">
                      <p><strong>Name:</strong> {s.name}</p>
                      <p><strong>Capacity:</strong> {s.capacityTons} Tons</p>
                      <p><strong>Rent/Day:</strong> {fmt(s.rentPerDay)}</p>
                      <p><strong>Location:</strong> {s.location}</p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <input type="text" placeholder="Name" className="border p-1 rounded" value={storageForm.name} onChange={e => setStorageForm(prev => ({ ...prev, name: e.target.value }))} />
                      <input type="text" placeholder="Phone" className="border p-1 rounded" value={storageForm.phone} onChange={e => setStorageForm(prev => ({ ...prev, phone: e.target.value }))} />
                      <input type="number" placeholder="Days" className="border p-1 rounded" value={storageForm.days} onChange={e => setStorageForm(prev => ({ ...prev, days: e.target.value }))} />
                      <button onClick={() => { setStorageForm(prev => ({ ...prev, storageId: s.id })); handleStorageBook(); }} className="bg-yellow-400 py-1 rounded">Book</button>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Partnership */}
            {modalMode === 'partnership' && selected.programs && (
              <>
                <h3 className="text-2xl font-bold mb-3">{selected.title}</h3>
                {selected.programs.map(p => (
                  <div key={p.id} className="border rounded p-3 mb-3 flex flex-col md:flex-row md:items-center gap-3">
                    <div className="flex-1">
                      <p><strong>Name:</strong> {p.name}</p>
                      <p><strong>Share:</strong> {p.share}</p>
                      <p><strong>Min Area Required:</strong> {p.minArea}</p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <input type="text" placeholder="Name" className="border p-1 rounded" value={partnershipForm.name} onChange={e => setPartnershipForm(prev => ({ ...prev, name: e.target.value }))} />
                      <input type="text" placeholder="Phone" className="border p-1 rounded" value={partnershipForm.phone} onChange={e => setPartnershipForm(prev => ({ ...prev, phone: e.target.value }))} />
                      <button onClick={() => { setPartnershipForm(prev => ({ ...prev, programId: p.id })); handlePartnership(); }} className="bg-yellow-400 py-1 rounded">Apply</button>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Global Assets */}
            {modalMode === 'global' && selected.global && (
              <>
                <h3 className="text-2xl font-bold mb-3">{selected.title}</h3>
                {selected.global.map(g => (
                  <div key={g.id} className="border rounded p-3 mb-3 flex justify-between items-center">
                    <div>
                      <p><strong>Country:</strong> {g.country}</p>
                      <p><strong>Area:</strong> {g.area}</p>
                      <p><strong>Price (USD):</strong> ${g.priceUSD.toLocaleString()}</p>
                    </div>
                    <button onClick={() => alert(`Interest noted for ${g.country} (dummy)`)} className="bg-yellow-400 py-1 px-3 rounded">Invest</button>
                  </div>
                ))}
              </>
            )}

            {/* Post New Asset */}
            {modalMode === 'post' && (
              <>
                <h3 className="text-2xl font-bold mb-3">Post Your Manufacturing Asset</h3>
                <div className="flex flex-col gap-2">
                  <input type="text" placeholder="Title" className="border p-2 rounded" value={postAssetForm.title} onChange={e => setPostAssetForm(prev => ({ ...prev, title: e.target.value }))} />
                  <textarea placeholder="Description" className="border p-2 rounded" value={postAssetForm.desc} onChange={e => setPostAssetForm(prev => ({ ...prev, desc: e.target.value }))} />
                  <input type="text" placeholder="Contact Info" className="border p-2 rounded" value={postAssetForm.contact} onChange={e => setPostAssetForm(prev => ({ ...prev, contact: e.target.value }))} />
                  <button onClick={handlePostAsset} className="bg-yellow-400 py-2 rounded mt-2">Submit Asset</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
